"use client";

import { IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldDescription } from "./FieldDescription";
import { calculateApiNodeId, computeNodeStepNumbers, extractWorkflowVariables, getFallbackVariableDisplayLabel, isValidVariable, resolveVariableValue } from "./extract-workflow-variables";
import { getFriendlyLabel, getPropKey } from "@/components/ui/workflow-step-row/components/VariablePill";
import { usePropValues } from "../PropValuesContext";
import { useRunExecutionStore } from "@/stores/runExecutionStore";
import { createVariableSpan, getCurrentWordInContentEditable, getVariableBeforeCursor, insertVariableAtCursor, serializeContentEditable, VARIABLE_ATTR, ZERO_WIDTH_SPACE } from "./variable-textarea-utils";
import { useInputSchema } from "./InputSchemaContext";
import { VariablePillPopover } from "./VariablePillPopover";
import type { Edge, Node } from "@xyflow/react";
interface NodeDefinition {
  type: string;
  resultSchema?: {
    properties?: Record<string, {
      description?: string;
      type?: string;
    }>;
  };
}
interface VariableInputProps {
  label: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  currentNodeId?: string;
  allNodes?: Node[];
  allEdges?: Edge[];
  placeholder?: string;
  nodeDefinitions?: NodeDefinition[];
  /** Auto-show the variable dropdown on mount (used by withVariableToggle) */
  autoShowDropdown?: boolean;
}
export default function VariableInput({
  label,
  description,
  value,
  onChange,
  required = false,
  currentNodeId = "",
  allNodes = [],
  allEdges = [],
  placeholder: placeholderProp,
  nodeDefinitions = [],
  autoShowDropdown = false
}: VariableInputProps) {
  const hasWorkflowContext = !!currentNodeId && allNodes.length > 0;
  const placeholder = placeholderProp ?? (hasWorkflowContext ? "Type {{ to insert variables..." : "Type your answer...");
  const inputSchema = useInputSchema();
  const propValues = usePropValues();
  const nodeStates = useRunExecutionStore(state => state.currentRun?.nodeStates);
  const editableRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [dropdownPlacement, setDropdownPlacement] = useState<"above" | "below">("below");
  const [currentTrigger, setCurrentTrigger] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const commandListRef = useRef<HTMLDivElement>(null);
  const isUpdatingRef = useRef(false);
  const lastValueRef = useRef(value);
  const [activePill, setActivePill] = useState<HTMLElement | null>(null);

  // Compute step numbers for display labels (BFS from root)
  const stepNumbers = useMemo(() => computeNodeStepNumbers(allNodes, allEdges), [allNodes, allEdges]);

  // Extract available variables from workflow (flat structure for inline dropdown and validation)
  const availableVariables = useMemo(() => {
    if (!currentNodeId || allNodes.length === 0) {
      return [];
    }
    return extractWorkflowVariables(currentNodeId, allNodes, allEdges, nodeDefinitions, undefined, inputSchema, stepNumbers);
  }, [currentNodeId, allNodes, allEdges, nodeDefinitions, inputSchema, stepNumbers]);

  // Build lookup: variableId -> displayLabel
  const displayLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const v of availableVariables) {
      if (v.displayLabel) map.set(v.id, v.displayLabel);
    }
    return map;
  }, [availableVariables]);

  // Callback to get display text for a variable (friendly name + optional run value)
  const getDisplayText = useCallback((variableId: string): string | undefined => {
    const inner = variableId.slice(2, -2);
    let label = displayLabelMap.get(variableId);
    // Fallback: compute friendly label for $ and @ variables not in the extracted list
    if (!label) {
      label = getFriendlyLabel(inner) ?? getFallbackVariableDisplayLabel(variableId, allNodes, allEdges, stepNumbers) ?? undefined;
    }
    if (!label) return undefined;

    // For $props variables, show the resolved value alongside the prop name
    const propKey = getPropKey(inner);
    if (propKey && propValues) {
      const val = propValues[propKey];
      if (val !== undefined && val !== null && val !== "") {
        return `${propKey}: ${String(val)}`;
      }
    }
    const runVal = resolveVariableValue(variableId, nodeStates);
    return runVal != null ? `${label} = ${runVal}` : label;
  }, [displayLabelMap, nodeStates, propValues, allNodes, allEdges, stepNumbers]);
  const isRenderableVariable = useCallback((variableId: string) => isValidVariable(variableId, availableVariables) || getFallbackVariableDisplayLabel(variableId, allNodes, allEdges, stepNumbers) !== undefined, [availableVariables, allNodes, allEdges, stepNumbers]);

  // Custom parser that doesn't add line breaks (single-line input)
  const parseStringToFragmentSingleLine = useCallback((val: string) => {
    const fragment = document.createDocumentFragment();

    // Safety check: ensure val is a string (use JSON.stringify for objects to avoid "[object Object]")
    if (typeof val !== "string") {
      console.warn("VariableInput: value is not a string, converting:", typeof val, val);
      val = val == null ? "" : typeof val === "object" ? JSON.stringify(val) : String(val);
    }
    const variableRegex = /({{[^}]+}})/g;
    const parts = val.split(variableRegex);
    let lastNodeWasVariable = false;
    parts.forEach(part => {
      if (!part) return;
      if (part.startsWith("{{") && part.endsWith("}}")) {
        const isValid = isRenderableVariable(part);
        const displayText = getDisplayText(part);
        const span = createVariableSpan(part, isValid, displayText);
        fragment.appendChild(span);
        lastNodeWasVariable = true;
      } else {
        // For single-line, replace newlines with spaces
        const cleanPart = part.replace(/\n/g, " ");
        if (cleanPart) {
          fragment.appendChild(document.createTextNode(cleanPart));
          lastNodeWasVariable = false;
        }
      }
    });

    // Add ZWSP after last variable for cursor anchoring
    if (lastNodeWasVariable) {
      fragment.appendChild(document.createTextNode(ZERO_WIDTH_SPACE));
    }
    return fragment;
  }, [getDisplayText, isRenderableVariable]);

  // Sync DOM with value prop when it changes externally
  useEffect(() => {
    if (!editableRef.current || isUpdatingRef.current) return;

    // Only update DOM if value actually changed from external source
    const currentContent = serializeContentEditable(editableRef.current);
    if (currentContent === value) {
      lastValueRef.current = value;
      return;
    }

    // Clear and rebuild content
    editableRef.current.innerHTML = "";
    setActivePill(null); // Clear stale DOM reference
    const fragment = parseStringToFragmentSingleLine(value);
    editableRef.current.appendChild(fragment);
    lastValueRef.current = value;
  }, [value, parseStringToFragmentSingleLine]);

  // Refresh pill display text when run values change (nodeStates updates)
  useEffect(() => {
    if (!editableRef.current) return;
    const pills = editableRef.current.querySelectorAll(`[data-variable]`);
    pills.forEach(pill => {
      const varId = pill.getAttribute("data-variable");
      if (varId) {
        const newText = getDisplayText(varId) || varId;
        // Update the text span child (first child), not the whole pill
        const textSpan = pill.querySelector(".variable-text");
        const target = textSpan || pill;
        if (target.textContent !== newText) {
          target.textContent = newText;
        }
      }
    });
  }, [getDisplayText]);

  // Handle input changes
  const handleInput = useCallback(() => {
    if (!editableRef.current) return;
    isUpdatingRef.current = true;

    // Serialize content and call onChange
    const newValue = serializeContentEditable(editableRef.current);
    if (newValue !== lastValueRef.current) {
      lastValueRef.current = newValue;
      onChange(newValue);
    }

    // Check for variable trigger
    const {
      word
    } = getCurrentWordInContentEditable(editableRef.current);
    if (word.startsWith("{{")) {
      // Check available space for dropdown placement
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - containerRect.bottom;
        const spaceAbove = containerRect.top;
        const showAbove = spaceBelow < 220 && spaceAbove > 220;
        setDropdownPlacement(showAbove ? "above" : "below");
      }
      setDropdownVisible(true);
      setCurrentTrigger(word);
    } else {
      setDropdownVisible(false);
      setCurrentTrigger("");
    }
    isUpdatingRef.current = false;
  }, [onChange]);

  // Handle variable selection from dropdown
  const onVariableSelect = useCallback((variableId: string) => {
    if (!editableRef.current) return;
    const isValid = isRenderableVariable(variableId);
    const displayText = getDisplayText(variableId);
    insertVariableAtCursor(editableRef.current, variableId, isValid, displayText);
    setDropdownVisible(false);
    setCurrentTrigger("");

    // Trigger onChange with new value
    const newValue = serializeContentEditable(editableRef.current);
    lastValueRef.current = newValue;
    onChange(newValue);

    // Refocus
    editableRef.current.focus();
  }, [onChange, getDisplayText, isRenderableVariable]);

  // Filter variables for dropdown (must be before handleKeyDown which uses it)
  const searchQuery = currentTrigger.replace("{{", "").toLowerCase();
  const filteredVariables = useMemo(() => availableVariables.filter(v => {
    if (!searchQuery) return true;
    // Extract field name: "{{root.result.price}}" → "price"
    const inner = v.id.slice(2, -2);
    const fieldName = inner.split(".").pop()?.toLowerCase() || "";
    return fieldName.includes(searchQuery) || v.label.toLowerCase().includes(searchQuery) || (v.displayLabel?.toLowerCase().includes(searchQuery) ?? false);
  }), [searchQuery, availableVariables]);

  // Remove a variable span and its adjacent ZWSP, then serialize and call onChange
  const removeVariableSpan = useCallback((variableSpan: HTMLElement) => {
    if (!editableRef.current) return;
    const parent = variableSpan.parentNode;
    if (!parent) return;

    // Remove adjacent ZWSP-only text node
    const nextSibling = variableSpan.nextSibling;
    if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE && (nextSibling.textContent || "").replace(/[\u200B\s]/g, "") === "") {
      nextSibling.remove();
    }
    variableSpan.remove();
    const newValue = serializeContentEditable(editableRef.current);
    lastValueRef.current = newValue;
    onChange(newValue);
  }, [onChange]);

  // Delegated click handler — detect clicks on variable pills to show popover
  const handleEditableClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const pill = target.closest(`[${VARIABLE_ATTR}]`) as HTMLElement | null;
    if (pill) {
      e.preventDefault();
      e.stopPropagation();
      setActivePill(prev => prev === pill ? null : pill);
    }
  }, []);

  // Handle keydown events
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    // Keyboard navigation when dropdown is visible
    if (dropdownVisible && filteredVariables.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredVariables.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredVariables.length) % filteredVariables.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const selected = filteredVariables[selectedIndex] ?? filteredVariables[0];
        if (selected) onVariableSelect(selected.id);
        return;
      }
    }

    // Prevent Enter key in single-line input
    if (e.key === "Enter") {
      e.preventDefault();
      return;
    }

    // Handle backspace on variables
    if (e.key === "Backspace" && editableRef.current) {
      const variableSpan = getVariableBeforeCursor(editableRef.current);
      if (variableSpan) {
        e.preventDefault();
        const parent = variableSpan.parentNode;
        if (parent) {
          // Capture sibling reference before DOM mutation
          const prevSibling = variableSpan.previousSibling;
          removeVariableSpan(variableSpan);

          // Position cursor where variable was (after DOM mutation)
          const range = document.createRange();
          const selection = window.getSelection();
          if (prevSibling && prevSibling.isConnected) {
            range.setStartAfter(prevSibling);
          } else {
            range.setStart(parent, 0);
          }
          range.collapse(true);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
        return;
      }
    }

    // Handle dropdown dismiss
    if (dropdownVisible && e.key === "Escape") {
      e.preventDefault();
      setDropdownVisible(false);
      setCurrentTrigger("");
    }
  }, [dropdownVisible, filteredVariables, selectedIndex, onVariableSelect, removeVariableSpan]);

  // Handle paste - strip HTML and parse variables (single line)
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    let text = e.clipboardData.getData("text/plain");
    if (!text || !editableRef.current) return;

    // Replace newlines with spaces for single-line input
    text = text.replace(/\n/g, " ");

    // Insert at cursor
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();

    // Parse the pasted text for variables
    const fragment = parseStringToFragmentSingleLine(text);
    range.insertNode(fragment);

    // Move cursor to end of pasted content
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);

    // Trigger onChange
    const newValue = serializeContentEditable(editableRef.current);
    lastValueRef.current = newValue;
    onChange(newValue);
  }, [onChange, parseStringToFragmentSingleLine]);

  // Handle blur
  const handleBlur = useCallback(() => {
    // Delay to allow dropdown clicks
    setTimeout(() => {
      setDropdownVisible(false);
      setCurrentTrigger("");
    }, 200);
  }, []);

  // Prevent dropdown blur
  const handleDropdownMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Fire-once on mount when autoShowDropdown becomes true; deps intentionally
  // minimal (ref guard prevents re-fire). Triggers only when user explicitly
  // toggles to variable mode (forceVariable), not on initial render with existing values.
  const autoShowFiredRef = useRef(false);
  useEffect(() => {
    if (!autoShowDropdown || autoShowFiredRef.current || availableVariables.length === 0) return;
    autoShowFiredRef.current = true;

    // Pre-fill with {{ trigger and show the dropdown immediately
    if (editableRef.current && !value) {
      editableRef.current.textContent = "{{";
      editableRef.current.focus();
      // Place cursor at end of {{
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(editableRef.current);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
      // Sync value with parent
      lastValueRef.current = "{{";
      onChange("{{");
      setCurrentTrigger("{{");
    }
    setDropdownVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omits onChange, value, setCurrentTrigger, setDropdownVisible: fire-once effect guarded by autoShowFiredRef
  }, [autoShowDropdown, availableVariables.length]);

  // Reset keyboard selection when search query changes or dropdown opens
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery, dropdownVisible]);

  // Scroll selected item into view
  useEffect(() => {
    if (!dropdownVisible || !commandListRef.current) return;
    const selected = commandListRef.current.querySelector('[data-highlighted="true"]');
    selected?.scrollIntoView({
      block: "nearest"
    });
  }, [selectedIndex, dropdownVisible]);

  // Extract unique source nodes from variable references in the current value
  const usedVariableSources = useMemo(() => {
    if (!value || typeof value !== "string" || allNodes.length === 0) return [];

    // Match all variable patterns like {{nodeId.result.field}} or {{nodeId.payload.field}}
    const variableRegex = /\{\{([^.}]+)\.[^}]+\}\}/g;
    const nodeIds = new Set<string>();
    let match;
    while ((match = variableRegex.exec(value)) !== null) {
      const nodeId = match[1];
      // Skip special variables like $item, $index, $props, @field
      if (!nodeId.startsWith("$") && !nodeId.startsWith("@")) {
        nodeIds.add(nodeId);
      }
    }

    // Map node IDs to node objects with display names
    // Use calculateApiNodeId to match variable IDs to nodes (same logic used when generating variables)
    return Array.from(nodeIds).map(varNodeId => {
      // Find the node by calculating its API node ID and comparing
      const node = allNodes.find(n => {
        const apiNodeId = calculateApiNodeId(n, allNodes, allEdges);
        return apiNodeId === varNodeId;
      });
      if (!node) return null;
      return {
        node,
        displayName: node.data.name as string || varNodeId,
        nodeId: varNodeId
      };
    }).filter((item): item is NonNullable<typeof item> => item !== null);
  }, [value, allNodes, allEdges]);
  return <div className="space-y-2">
      {label && <label className="field-label flex items-center gap-2">
          <span>{label}</span>
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>}
      {description && <FieldDescription text={description} />}
      <div ref={containerRef} className="relative">
        <div className="group/input border-ds-neutral-300 dark:border-ds-neutral-700 bg-ds-surface-card shadow-ds-soft relative overflow-hidden rounded-lg border focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20">
          {/* Contenteditable div - single line */}
          <div ref={editableRef} contentEditable suppressContentEditableWarning onInput={handleInput} onKeyDown={handleKeyDown} onPaste={handlePaste} onBlur={handleBlur} onClick={handleEditableClick} className="text-ds-text-primary empty:before:text-ds-text-disabled dark:empty:before:text-ds-text-muted w-full overflow-x-auto border-0 bg-transparent py-2 pl-3 pr-8 font-mono text-sm leading-relaxed caret-zinc-900 outline-none empty:before:content-[attr(data-placeholder)] dark:text-zinc-100 dark:caret-zinc-100" data-placeholder={placeholder} style={{
          whiteSpace: "nowrap"
        }} />

          {/* Clear button - visible when input has content */}
          {value && <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => {
          if (editableRef.current) {
            editableRef.current.innerHTML = "";
          }
          lastValueRef.current = "";
          onChange("");
        }} className="text-ds-text-disabled hover:text-ds-text-subtle dark:text-ds-text-muted absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover/input:opacity-100 dark:hover:text-zinc-300" aria-label="Clear input">
              <IconXmarkOutline18 className="h-3.5 w-3.5" />
            </button>}
        </div>

        {/* Variable Suggestions Dropdown - Grouped by category */}
        {dropdownVisible && filteredVariables.length > 0 && <div ref={dropdownRef} onMouseDown={handleDropdownMouseDown} className={`absolute left-0 z-50 ${dropdownPlacement === "above" ? "bottom-full mb-1" : "top-full mt-1"}`}>
            <Command shouldFilter={false} className="border-ds-border-default bg-ds-surface-card max-h-[280px] w-[360px] overflow-auto rounded-lg border shadow-xl">
              <CommandList ref={commandListRef}>
                {Object.entries(filteredVariables.reduce((acc, v) => {
              if (!acc[v.category]) acc[v.category] = [];
              acc[v.category].push(v);
              return acc;
            }, {} as Record<string, typeof filteredVariables>)).map(([category, vars]) => <div key={category} className="border-ds-neutral-100 dark:border-ds-neutral-700/50 border-b last:border-0">
                    <div className="text-ds-text-secondary dark:bg-ds-neutral-900 sticky top-0 z-10 bg-zinc-50 px-3 py-2 text-xs font-semibold">
                      {category}
                    </div>
                    <CommandGroup>
                      {vars.map(variable => {
                  const runVal = resolveVariableValue(variable.id, nodeStates);
                  return <CommandItem key={variable.id} value={variable.id} onSelect={() => onVariableSelect(variable.id)} data-highlighted={variable.id === filteredVariables[selectedIndex]?.id ? "true" : undefined} className={`cursor-pointer px-3 py-2 ${variable.id === filteredVariables[selectedIndex]?.id ? "dark:!bg-ds-neutral-700 !bg-blue-50" : ""}`}>
                            <div className="flex flex-col gap-1">
                              <span className="rounded-ds-xs inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-50 to-indigo-100 px-2 py-1 font-mono text-sm text-blue-700 dark:from-blue-950/30 dark:to-indigo-950/30 dark:text-blue-400">
                                {variable.displayLabel || variable.id}
                              </span>
                              <span className="label-xs">{variable.label}</span>
                              {runVal != null && <span className="font-mono text-xs text-green-600 dark:text-green-400">= {runVal}</span>}
                            </div>
                          </CommandItem>;
                })}
                    </CommandGroup>
                  </div>)}
              </CommandList>
            </Command>
          </div>}
      </div>
      {/* Pill popover — shown when a variable pill is clicked */}
      <VariablePillPopover pill={activePill} onDelete={() => {
      if (activePill) {
        removeVariableSpan(activePill);
        setActivePill(null);
      }
    }} onClose={() => setActivePill(null)} />
      {/* Using variables from - show source nodes when variables are used */}
      {usedVariableSources.length > 0 && <div className="scrollbar-none flex items-center gap-1.5 overflow-x-auto">
          <span className="micro-label shrink-0">Using</span>
          {usedVariableSources.map(source => <span key={source.nodeId} className="text-2xs inline-flex shrink-0 items-center rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 font-medium text-blue-700 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300">
              {source.displayName}
            </span>)}
        </div>}
    </div>;
}